/**
 * SuccessScreen Component
 *
 * Celebratory success screen displayed after completing important actions.
 * Features:
 * - Animated checkmark icon
 * - Customizable title and message
 * - Primary and secondary action buttons
 * - Optional confetti or celebration animation (can be added later)
 */

import React from 'react';
import { CheckCircle2, ArrowRight, Home } from 'lucide-react';
import { Button } from '../common/Button';

export interface SuccessScreenProps {
  title: string;
  message?: string;
  icon?: React.ReactNode;
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  showAnimation?: boolean;
  variant?: 'default' | 'celebration';
  className?: string;
}

export const SuccessScreen: React.FC<SuccessScreenProps> = ({
  title,
  message,
  icon,
  primaryAction,
  secondaryAction,
  showAnimation = true,
  className = '',
}) => {
  const defaultIcon = (
    <CheckCircle2 className="w-20 h-20 text-success-600" />
  );

  return (
    <div className={`flex flex-col items-center justify-center min-h-[60vh] px-4 py-12 ${className}`}>
      {/* Icon */}
      <div
        className={`mb-8 ${
          showAnimation
            ? 'animate-scale-in'
            : ''
        }`}
      >
        <div className="relative">
          {/* Success Icon */}
          <div className="relative z-10">
            {icon || defaultIcon}
          </div>

          {/* Background Circle Animation */}
          {showAnimation && (
            <div className="absolute inset-0 -z-10">
              <div className="w-full h-full bg-success-100 rounded-full animate-ping-slow opacity-20" />
            </div>
          )}
        </div>
      </div>

      {/* Title */}
      <h1
        className={`text-3xl font-bold text-neutral-900 mb-4 text-center ${
          showAnimation ? 'animate-fade-in-up' : ''
        }`}
        style={{ animationDelay: '0.2s' }}
      >
        {title}
      </h1>

      {/* Message */}
      {message && (
        <p
          className={`text-lg text-neutral-600 max-w-md text-center mb-10 ${
            showAnimation ? 'animate-fade-in-up' : ''
          }`}
          style={{ animationDelay: '0.3s' }}
        >
          {message}
        </p>
      )}

      {/* Actions */}
      {(primaryAction || secondaryAction) && (
        <div
          className={`flex flex-col sm:flex-row items-center gap-4 ${
            showAnimation ? 'animate-fade-in-up' : ''
          }`}
          style={{ animationDelay: '0.4s' }}
        >
          {primaryAction && (
            <Button
              variant="primary"
              size="lg"
              onClick={primaryAction.onClick}
            >
              {primaryAction.label}
              {primaryAction.icon || <ArrowRight className="w-5 h-5 ml-2" />}
            </Button>
          )}

          {secondaryAction && (
            <Button
              variant="outline"
              size="lg"
              onClick={secondaryAction.onClick}
            >
              <Home className="w-5 h-5 mr-2" />
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SuccessScreen;
